"use client";

import { ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function PostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="7218" className="mx-auto max-w-4xl space-y-8">
        <div className="h-12 w-3/4 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-6 w-1/2 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-[400px] w-full bg-muted/30 animate-pulse rounded-md" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 w-full bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 w-5/6 bg-muted/30 animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl text-center py-16">
        <p className="text-muted-foreground">Unable to load the article at this time.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl text-center py-16">
        <p className="text-muted-foreground">No article found.</p>
      </div>
    );
  }

  const post = posts[0];
  const author = post.author;
  const categories = post.categories || [];
  const tags = post.tags || [];

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div className="mx-auto max-w-4xl">
      {}
      <div className="mb-10">
        <Link
          to="/ShopPage"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="size-4" />
          <span>Back to our flavors</span>
        </Link>
      </div>

      {}
      {categories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-wider border border-border text-primary"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1 className="font-default text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground mb-8">
        {post.title}
      </h1>

      {}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-mono text-muted-foreground mb-12">
        {author?.name && (
          <div className="flex items-center gap-2">
            <User className="size-4" />
            <span>{author.name}</span>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <span>{formattedDate}</span>
          </div>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="mb-16 overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {}
      <article className="font-default text-base md:text-lg leading-relaxed text-foreground/90 max-w-3xl mx-auto">
        {post.excerpt && (
          <p className="text-xl md:text-2xl font-default italic text-foreground mb-10 leading-snug">
            {post.excerpt.replace(/<[^>]*>/g, "")}
          </p>
        )}
        {post.content ? (
          <div
            className="prose prose-lg max-w-none [&>p]:mb-6 [&>h2]:font-default [&>h2]:text-3xl [&>h2]:mt-12 [&>h2]:mb-4 [&>h3]:font-default [&>h3]:text-2xl [&>h3]:mt-8 [&>h3]:mb-3 [&>a]:text-primary [&>a]:underline [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>blockquote]:border-l-2 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:my-8"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        ) : (
          <p>No content available.</p>
        )}
      </article>

      {}
      {tags.length > 0 && (
        <div className="mt-16 max-w-3xl mx-auto">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
              Tagged
            </span>
            {tags.map((tag, i) => (
              <span
                data-index={i}
                key={tag.id}
                className="text-xs font-mono uppercase tracking-wider text-foreground border-b border-border hover:border-primary transition-colors"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <Separator className="my-16 bg-border" />

      {}
      {author && (
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-6">
            Meet the maker
          </p>
          <Card className="bg-card border border-border shadow-md rounded-lg">
            <CardContent className="flex flex-col sm:flex-row items-start gap-6 pt-6">
              <Avatar className="size-20 rounded-lg shrink-0">
                {author.avatar?.["96"] && (
                  <AvatarImage src={author.avatar["96"]} alt={author.name} className="rounded-lg" />
                )}
                <AvatarFallback className="rounded-lg bg-muted text-foreground font-default text-2xl">
                  {author.name?.charAt(0) || "S"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-default text-2xl text-foreground mb-2">{author.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Our lead artisan at SCOOP, dedicated to crafting small-batch, premium ice cream using only the finest natural ingredients.
                </p>
                {author.link && (
                  <Link
                    to={author.link}
                    className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-primary hover:text-secondary transition-colors"
                  >
                    <span>View more stories</span>
                    <ArrowRight className="size-4" />
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function RelatedPosts() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-4">
            <div className="h-56 w-full bg-muted/30 animate-pulse rounded-lg" />
            <div className="h-6 w-3/4 bg-muted/30 animate-pulse rounded-md" />
            <div className="h-4 w-full bg-muted/30 animate-pulse rounded-md" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-muted-foreground text-center">No other stories available.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
      {posts.map((post, i) => {
        const formattedDate = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        return (
          <Link
            key={post.id}
            to={post.link || "#"}
            data-index={i}
            className="group block"
          >
            <article className="space-y-4">
              {post.featuredImage ? (
                <div className="overflow-hidden aspect-[4/3] bg-muted rounded-lg">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] bg-muted flex items-center justify-center rounded-lg">
                  <span className="font-default text-6xl text-muted-foreground/30">S</span>
                </div>
              )}
              <div className="space-y-3">
                {post.categories && post.categories.length > 0 && (
                  <span className="inline-block text-xs font-mono uppercase tracking-wider text-primary">
                    {post.categories[0].name}
                  </span>
                )}
                <h3 className="font-default text-2xl leading-tight text-foreground group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
                {formattedDate && (
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    {formattedDate}
                  </p>
                )}
              </div>
            </article>
          </Link>
        );
      })}
    </div>
  );
}

export default function () {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc",
  });

  return (
    <section
      id="single-post-template-section"
      className="bg-background text-foreground py-20 md:py-28 px-4 md:px-8"
    >
      <div className="max-w-7xl mx-auto">
        <PostsProvider wp_query={wp_query}>
          <PostContent />
        </PostsProvider>

        {}
        <div className="mt-24 md:mt-32 max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-primary mb-4">
                Continue Reading
              </p>
              <h2 className="font-default text-4xl md:text-5xl text-foreground leading-tight">
                More from the SCOOP studio
              </h2>
            </div>
            <Link to="/ShopPage">
              <Button
                variant="outline"
                className="border-border bg-transparent text-foreground hover:bg-accent rounded-lg font-mono uppercase tracking-wider text-xs"
              >
                View all flavors
                <ArrowRight className="size-4 ml-2" />
              </Button>
            </Link>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
            <RelatedPosts />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}